import { apiService } from './api';
import { joinUrl } from './joinUrl';

export interface HealthCheckResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  services: {
    [key: string]: {
      status: 'operational' | 'degraded' | 'outage' | 'maintenance';
      responseTime: number;
      uptime: number;
      lastChecked: string;
    };
  };
  overall: {
    uptime: number;
    responseTime: number;
    errorRate: number;
  };
}

export interface ServiceHealth {
  name: string;
  status: 'operational' | 'degraded' | 'outage' | 'maintenance';
  description: string;
  responseTime: number;
  uptime: number;
  lastChecked: string;
}

class HealthService {
  /**
   * Check the overall health of the system
   */
  async checkSystemHealth(): Promise<HealthCheckResponse> {
    try {
      const data = await apiService.get('health', { isPublic: true });
      return this.transformHealthData(data);
    } catch (error) {
      console.error('Health check failed:', error);
      return this.getFallbackHealthData();
    }
  }

  /**
   * Check specific service health
   */
  async checkServiceHealth(serviceName: string): Promise<ServiceHealth> {
    try {
      const data = await apiService.get(`health/${serviceName}`, { isPublic: true });
      return this.transformServiceData(serviceName, data);
    } catch (error) {
      console.error(`Service health check failed for ${serviceName}:`, error);
      return this.getFallbackServiceData(serviceName);
    }
  }

  /**
   * Check multiple services in parallel
   */
  async checkMultipleServices(serviceNames: string[]): Promise<ServiceHealth[]> {
    const promises = serviceNames.map(name => this.checkServiceHealth(name));
    return Promise.allSettled(promises).then(results => 
      results.map(result => 
        result.status === 'fulfilled' ? result.value : this.getFallbackServiceData('unknown')
      )
    );
  }

  /**
   * Get incident history
   */
  async getIncidents(): Promise<any[]> {
    try {
      const data = await apiService.get('incidents', { isPublic: true });
      return data.incidents || [];
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
      return [];
    }
  }

  /**
   * Transform raw health data into our format
   */
  private transformHealthData(data: any): HealthCheckResponse {
    return {
      status: data.status || 'healthy',
      timestamp: data.timestamp || new Date().toISOString(),
      services: data.services || {},
      overall: {
        uptime: data.uptime || 99.9,
        responseTime: data.responseTime || 100,
        errorRate: data.errorRate || 0.01,
      },
    };
  }

  /**
   * Transform service data into our format
   */
  private transformServiceData(serviceName: string, data: any): ServiceHealth {
    return {
      name: serviceName,
      status: data.status || 'operational',
      description: data.description || `${serviceName} service`,
      responseTime: data.responseTime || 0,
      uptime: data.uptime || 100,
      lastChecked: data.lastChecked || new Date().toISOString(),
    };
  }

  /**
   * Get fallback health data when API is unavailable
   */
  private getFallbackHealthData(): HealthCheckResponse {
    return {
      status: 'degraded',
      timestamp: new Date().toISOString(),
      services: {
        api: {
          status: 'outage',
          responseTime: 0,
          uptime: 0,
          lastChecked: new Date().toISOString(),
        },
      },
      overall: {
        uptime: 0,
        responseTime: 0,
        errorRate: 1,
      },
    };
  }

  /**
   * Get fallback service data when service is unavailable
   */
  private getFallbackServiceData(serviceName: string): ServiceHealth {
    return {
      name: serviceName,
      status: 'outage',
      description: `${serviceName} service`,
      responseTime: 0,
      uptime: 0,
      lastChecked: new Date().toISOString(),
    };
  }

  /**
   * Ping a service to check if it's responding
   */
  async pingService(endpoint: string): Promise<{ success: boolean; responseTime: number }> {
    const startTime = Date.now();
    try {
      await apiService.get(endpoint, { isPublic: true });
      const responseTime = Date.now() - startTime;
      return { success: true, responseTime };
    } catch (error) {
      const responseTime = Date.now() - startTime;
      return { success: false, responseTime };
    }
  }

  /**
   * Get service definitions for the status page
   */
  getServiceDefinitions() {
    return [
      {
        name: 'API Gateway',
        endpoint: 'health',
        description: 'Core API endpoints and authentication',
      },
      {
        name: 'Database',
        endpoint: 'health/database',
        description: 'PostgreSQL database and connections',
      },
      {
        name: 'Email Service',
        endpoint: 'health/email',
        description: 'Transactional emails and notifications',
      },
      {
        name: 'Payment Processing',
        endpoint: 'health/payments',
        description: 'Stripe payment gateway integration',
      },
      {
        name: 'File Storage',
        endpoint: 'health/storage',
        description: 'Image and document storage',
      },
      {
        name: 'Search Engine',
        endpoint: 'health/search',
        description: 'Location and content search',
      },
    ];
  }
}

export const healthService = new HealthService();
export default healthService; 